import React, { useState } from 'react';
import { CheckCircle, XCircle, Calendar, User } from 'lucide-react';
import { Quotation, COST_CATEGORY_LABELS } from '../../types/billing';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Modal } from '../ui/Modal';
import { QuotationStatusBadge } from './PaymentStatusBadge';

interface QuotationApprovalViewProps {
  quotation: Quotation;
  onApprove: (quotationId: string) => void;
  onReject: (quotationId: string, reason: string) => void;
  isProcessing?: boolean;
}

export const QuotationApprovalView: React.FC<QuotationApprovalViewProps> = ({
  quotation,
  onApprove,
  onReject,
  isProcessing = false,
}) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (!rejectReason.trim()) return;
    onReject(quotation.quotation_id, rejectReason.trim());
    setShowRejectModal(false);
    setRejectReason('');
  };

  const isActionable = quotation.status === 'submitted';

  return (
    <>
      <Card
        title={`Quotation Review`}
        subtitle={`${quotation.quotation_id} from ${quotation.vendor_name}`}
        action={<QuotationStatusBadge status={quotation.status} />}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="p-3 bg-slate-950 rounded-lg border border-slate-800 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-sky-400" />
              <div>
                <p className="text-[10px] text-slate-500 font-medium">Submitted</p>
                <p className="font-semibold text-slate-200">{new Date(quotation.submitted_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="p-3 bg-slate-950 rounded-lg border border-slate-800 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-[10px] text-slate-500 font-medium">Valid Until</p>
                <p className="font-semibold text-slate-200">
                  {new Date(new Date(quotation.submitted_at).getTime() + quotation.validity_days * 86400000).toLocaleDateString()}
                </p>
              </div>
            </div>
            {quotation.approved_by && (
              <div className="p-3 bg-slate-950 rounded-lg border border-slate-800 flex items-center gap-3">
                <User className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-[10px] text-slate-500 font-medium">Approved By</p>
                  <p className="font-semibold text-slate-200">{quotation.approved_by}</p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-400 mb-2">Line Items</p>
            {quotation.items.map((item) => (
              <div key={item.item_id} className="flex justify-between items-center py-2 px-3 bg-slate-950 rounded-lg border border-slate-800 text-xs">
                <div>
                  <span className="text-slate-200">{item.description}</span>
                  <span className="text-slate-500 ml-2">({item.quantity} {item.unit} Ã— Rs {item.unit_price.toLocaleString()})</span>
                </div>
                <span className="text-slate-300 font-semibold">Rs {item.amount.toLocaleString()}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-700 pt-3 space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Subtotal</span>
              <span className="text-slate-300 font-medium">Rs {quotation.subtotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Tax (18%)</span>
              <span className="text-slate-300">Rs {quotation.tax_amount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm font-semibold pt-1 border-t border-slate-700">
              <span className="text-slate-200">Total</span>
              <span className="text-emerald-400">Rs {quotation.total.toLocaleString()}</span>
            </div>
          </div>

          {quotation.notes && (
            <div className="p-3 bg-slate-950 rounded-lg border border-slate-800">
              <p className="text-[10px] text-slate-500 font-medium mb-1">Notes</p>
              <p className="text-xs text-slate-300">{quotation.notes}</p>
            </div>
          )}

          {quotation.rejection_reason && (
            <div className="p-3 bg-rose-500/5 rounded-lg border border-rose-500/20">
              <p className="text-[10px] text-rose-400 font-medium mb-1">Rejection Reason</p>
              <p className="text-xs text-rose-300">{quotation.rejection_reason}</p>
            </div>
          )}

          {isActionable && (
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
              <Button variant="danger" onClick={() => setShowRejectModal(true)} icon={<XCircle className="w-4 h-4" />}>
                Reject with Reason
              </Button>
              <Button variant="primary" onClick={() => onApprove(quotation.quotation_id)} isLoading={isProcessing} icon={<CheckCircle className="w-4 h-4" />}>
                Approve Quotation
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Reject Quotation" footer={
        <>
          <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleReject} disabled={!rejectReason.trim()}>Reject</Button>
        </>
      }>
        <div className="space-y-3">
          <label className="text-xs text-slate-400 block">Reason for rejection</label>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            placeholder="Provide reason for rejection..."
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-sky-500 resize-none"
          />
        </div>
      </Modal>
    </>
  );
};
